import { useMutation, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Calendar, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TagMultiSelect } from '@/components/ui/tag-multi-select';
import { Textarea } from '@/components/ui/textarea';
import { queryClient, trpc } from '@/utils/trpc';

interface TransactionEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactionId: string | null;
  onSuccess?: () => void;
}

interface TransactionFormData {
  amount: string;
  type: 'debit' | 'credit';
  categoryId: string | null;
  description: string;
  date: string;
  notes: string;
  tags: string[];
  // Installment fields
  isInstallment: boolean;
  totalInstallments: number;
  installmentAmount: string;
  useInstallmentAmount: boolean; // If true, use installment amount; if false, use total amount
}

export function TransactionEditDialog({
  open,
  onOpenChange,
  transactionId,
  onSuccess,
}: TransactionEditDialogProps) {
  const [formData, setFormData] = useState<TransactionFormData>({
    amount: '',
    type: 'debit',
    categoryId: null,
    description: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    tags: [],
    // Installment fields
    isInstallment: false,
    totalInstallments: 2,
    installmentAmount: '',
    useInstallmentAmount: false,
  });
  const [initialCategoryId, setInitialCategoryId] = useState<string | null>(
    null
  );
  const [categoryTouched, setCategoryTouched] = useState(false);

  // Conversion state
  const [showConversionSection, setShowConversionSection] = useState(false);
  const [conversionData, setConversionData] = useState({
    totalInstallments: 2,
    installmentAmount: '',
    useInstallmentAmount: false,
  });

  // Tag suggestions handled by TagMultiSelect

  // Get transaction data
  const transaction = useQuery({
    ...trpc.transactions.get.queryOptions({ id: transactionId! }),
    enabled: !!transactionId && open,
  });

  // Get categories
  const categories = useQuery(trpc.categories.list.queryOptions({}));

  // Update and delete mutations
  const updateTransaction = useMutation(
    trpc.transactions.update.mutationOptions()
  );
  const deleteTransaction = useMutation(
    trpc.transactions.delete.mutationOptions()
  );
  const convertToInstallments = useMutation(
    trpc.transactions.convertToInstallments.mutationOptions()
  );

  // Populate form when transaction data loads
  useEffect(() => {
    if (transaction.data) {
      const t = transaction.data;
      const isInstallmentTransaction = t.totalInstallments > 1;

      setFormData({
        amount: isInstallmentTransaction
          ? (t.originalPurchaseAmount || t.amount).toString()
          : t.amount.toString(),
        type: t.type,
        categoryId: t.categoryId,
        description: t.description?.replace(/ \(\d+\/\d+\)$/, '') || '', // Remove installment suffix
        date: format(new Date(t.date), 'yyyy-MM-dd'),
        notes: t.notes || '',
        tags: t.tags || [],
        // Installment fields
        isInstallment: isInstallmentTransaction,
        totalInstallments: t.totalInstallments || 1,
        installmentAmount: isInstallmentTransaction ? t.amount.toString() : '',
        useInstallmentAmount: isInstallmentTransaction,
      });
      setInitialCategoryId(t.categoryId ?? null);
      setCategoryTouched(false);
    }
  }, [transaction.data]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transactionId) return;

    try {
      const resolvedCategoryId = categoryTouched
        ? formData.categoryId
        : initialCategoryId;

      // Calculate final amount based on installment settings
      let finalAmount = formData.amount;
      if (formData.isInstallment && formData.useInstallmentAmount) {
        finalAmount = formData.installmentAmount;
      }

      await updateTransaction.mutateAsync({
        id: transactionId,
        amount: finalAmount,
        type: formData.type,
        categoryId:
          resolvedCategoryId === undefined ? undefined : resolvedCategoryId,
        description: formData.description,
        date: new Date(formData.date).toISOString(),
        notes: formData.notes,
        tags: formData.tags,
        // Installment fields (for editing installment info)
        totalInstallments: formData.isInstallment
          ? formData.totalInstallments
          : 1,
        originalPurchaseAmount: formData.isInstallment ? formData.amount : null,
      });

      queryClient.invalidateQueries();
      onSuccess?.();
      onOpenChange(false);
      toast.success('Transação atualizada com sucesso!');
    } catch (error) {
      toast.error('Erro ao atualizar transação');
    }
  };

  const handleDelete = async () => {
    if (!transactionId) return;

    if (
      !confirm(
        'Tem certeza que deseja excluir esta transação? Esta ação não pode ser desfeita.'
      )
    ) {
      return;
    }

    try {
      await deleteTransaction.mutateAsync({ id: transactionId });
      queryClient.invalidateQueries();
      onSuccess?.();
      onOpenChange(false);
      toast.success('Transação excluída com sucesso!');
    } catch (error) {
      toast.error('Erro ao excluir transação');
    }
  };

  const handleConvertToInstallments = async () => {
    if (!transactionId) return;

    if (
      !confirm(
        `Tem certeza que deseja converter esta transação em ${conversionData.totalInstallments} parcelas? Isso criará múltiplas transações e a transação original será removida.`
      )
    ) {
      return;
    }

    try {
      const payload: any = {
        transactionId,
        totalInstallments: conversionData.totalInstallments,
      };

      if (conversionData.useInstallmentAmount && conversionData.installmentAmount) {
        payload.installmentAmount = conversionData.installmentAmount;
      }

      await convertToInstallments.mutateAsync(payload);
      queryClient.invalidateQueries();
      onSuccess?.();
      onOpenChange(false);
      toast.success(`Transação convertida em ${conversionData.totalInstallments} parcelas com sucesso!`);
    } catch (error) {
      toast.error('Erro ao converter transação');
    }
  };

  const setTags = (tags: string[]) => {
    setFormData((prev) => ({ ...prev, tags }));
  };

  const removeTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  };

  // Auto-calculate when installment fields change
  const handleInstallmentCalculation = (
    totalInstallments: number,
    amount: string,
    installmentAmount: string,
    useInstallmentAmount: boolean
  ) => {
    if (useInstallmentAmount && installmentAmount && totalInstallments > 0) {
      // Calculate total from installment amount
      const total = (Number(installmentAmount) * totalInstallments).toFixed(2);
      setFormData((prev) => ({ ...prev, amount: total }));
    } else if (!useInstallmentAmount && amount && totalInstallments > 0) {
      // Calculate installment from total amount
      const installment = (Number(amount) / totalInstallments).toFixed(2);
      setFormData((prev) => ({ ...prev, installmentAmount: installment }));
    }
  };

  // Auto-calculate when conversion fields change
  const handleConversionCalculation = (
    totalInstallments: number,
    amount: string,
    installmentAmount: string,
    useInstallmentAmount: boolean
  ) => {
    if (useInstallmentAmount && installmentAmount && totalInstallments > 0) {
      // We don't need to update anything here since user is providing the installment amount
    } else if (!useInstallmentAmount && amount && totalInstallments > 0) {
      // Calculate installment from current transaction amount
      const installment = (Number(amount) / totalInstallments).toFixed(2);
      setConversionData((prev) => ({ ...prev, installmentAmount: installment }));
    }
  };

  if (!transactionId) return null;

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5" />
            Editar Transação
          </DialogTitle>
          <DialogDescription>
            Modifique os dados da transação conforme necessário
          </DialogDescription>
        </DialogHeader>

        {transaction.isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div className="h-10 animate-pulse rounded bg-muted" key={i} />
            ))}
          </div>
        ) : transaction.error ? (
          <div className="py-4 text-center text-red-500">
            Erro ao carregar transação
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit}>
            {/* Amount and Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Valor</Label>
                <Input
                  id="amount"
                  min="0"
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, amount: e.target.value }))
                  }
                  placeholder="0.00"
                  required
                  step="0.01"
                  type="number"
                  value={formData.amount}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Tipo</Label>
                <Select
                  onValueChange={(value: 'debit' | 'credit') =>
                    setFormData((prev) => ({ ...prev, type: value }))
                  }
                  value={formData.type}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-red-500" />
                        Gasto
                      </div>
                    </SelectItem>
                    <SelectItem value="credit">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-green-500" />
                        Receita
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Input
                id="description"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Descreva a transação..."
                value={formData.description}
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">Categoria</Label>
              <Select
                onValueChange={(value) => {
                  setCategoryTouched(true);
                  setFormData((prev) => ({
                    ...prev,
                    categoryId: value === 'n/a' || !value ? null : value,
                  }));
                }}
                value={formData.categoryId || ''}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma categoria..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="n/a">
                    <span className="text-muted-foreground">Sem categoria</span>
                  </SelectItem>
                  {categories.data?.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      <div className="flex items-center gap-2">
                        <span>{category.icon}</span>
                        <span>{category.name}</span>
                        <Badge
                          className="text-xs"
                          style={{
                            borderColor: category.color,
                            color: category.color,
                          }}
                          variant="outline"
                        >
                          {category.type === 'expense' ? 'Gasto' : 'Receita'}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="date">Data</Label>
              <div className="relative">
                <Calendar className="absolute top-3 left-3 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-10"
                  id="date"
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, date: e.target.value }))
                  }
                  required
                  type="date"
                  value={formData.date}
                />
              </div>
            </div>

            {/* Installment Settings */}
            <div className="space-y-4 rounded-md border p-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={formData.isInstallment}
                  id="isInstallment"
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({
                      ...prev,
                      isInstallment: !!checked,
                    }))
                  }
                />
                <Label htmlFor="isInstallment">Transação Parcelada</Label>
              </div>

              {formData.isInstallment && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="totalInstallments">
                        Número de Parcelas
                      </Label>
                      <Input
                        id="totalInstallments"
                        max="60"
                        min="2"
                        onChange={(e) => {
                          const newInstallments = Number(e.target.value);
                          setFormData((prev) => ({
                            ...prev,
                            totalInstallments: newInstallments,
                          }));
                          handleInstallmentCalculation(
                            newInstallments,
                            formData.amount,
                            formData.installmentAmount,
                            formData.useInstallmentAmount
                          );
                        }}
                        type="number"
                        value={formData.totalInstallments}
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={formData.useInstallmentAmount}
                          id="useInstallmentAmount"
                          onCheckedChange={(checked) =>
                            setFormData((prev) => ({
                              ...prev,
                              useInstallmentAmount: !!checked,
                            }))
                          }
                        />
                        <Label htmlFor="useInstallmentAmount">
                          Informar valor da parcela
                        </Label>
                      </div>
                    </div>
                  </div>

                  {formData.useInstallmentAmount ? (
                    <div className="space-y-2">
                      <Label htmlFor="installmentAmount">
                        Valor da Parcela
                      </Label>
                      <Input
                        id="installmentAmount"
                        min="0"
                        onChange={(e) => {
                          const newInstallmentAmount = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            installmentAmount: newInstallmentAmount,
                          }));
                          handleInstallmentCalculation(
                            formData.totalInstallments,
                            formData.amount,
                            newInstallmentAmount,
                            true
                          );
                        }}
                        placeholder="0.00"
                        step="0.01"
                        type="number"
                        value={formData.installmentAmount}
                      />
                      {formData.installmentAmount &&
                        formData.totalInstallments > 1 && (
                          <p className="text-muted-foreground text-sm">
                            Total:{' '}
                            {formatCurrency(
                              Number(formData.installmentAmount) *
                                formData.totalInstallments
                            )}
                          </p>
                        )}
                    </div>
                  ) : (
                    formData.amount &&
                    formData.totalInstallments > 1 && (
                      <p className="text-muted-foreground text-sm">
                        Parcelas de:{' '}
                        {formatCurrency(
                          Number(formData.amount) / formData.totalInstallments
                        )}
                      </p>
                    )
                  )}
                </div>
              )}
            </div>

            {/* Convert to Installments Section */}
            {!formData.isInstallment && transaction.data && (
              <div className="space-y-4 rounded-md border p-4 border-orange-200 bg-orange-50">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-orange-900">Converter para Parcelamento</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowConversionSection(!showConversionSection)}
                  >
                    {showConversionSection ? 'Ocultar' : 'Mostrar'}
                  </Button>
                </div>

                {showConversionSection && (
                  <div className="space-y-3">
                    <p className="text-orange-700 text-sm">
                      Esta função converterá esta transação em múltiplas parcelas.
                      A transação original será removida e substituída por {conversionData.totalInstallments} parcelas.
                    </p>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="conversionInstallments">Número de Parcelas</Label>
                        <Input
                          id="conversionInstallments"
                          type="number"
                          min="2"
                          max="60"
                          value={conversionData.totalInstallments}
                          onChange={(e) => {
                            const newInstallments = Number(e.target.value);
                            setConversionData((prev) => ({
                              ...prev,
                              totalInstallments: newInstallments,
                            }));
                            handleConversionCalculation(
                              newInstallments,
                              formData.amount,
                              conversionData.installmentAmount,
                              conversionData.useInstallmentAmount
                            );
                          }}
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            checked={conversionData.useInstallmentAmount}
                            id="conversionUseInstallmentAmount"
                            onCheckedChange={(checked) =>
                              setConversionData((prev) => ({
                                ...prev,
                                useInstallmentAmount: !!checked,
                              }))
                            }
                          />
                          <Label htmlFor="conversionUseInstallmentAmount">
                            Informar valor da parcela
                          </Label>
                        </div>
                      </div>
                    </div>

                    {conversionData.useInstallmentAmount ? (
                      <div className="space-y-2">
                        <Label htmlFor="conversionInstallmentAmount">Valor da Parcela</Label>
                        <Input
                          id="conversionInstallmentAmount"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={conversionData.installmentAmount}
                          onChange={(e) => {
                            const newInstallmentAmount = e.target.value;
                            setConversionData((prev) => ({
                              ...prev,
                              installmentAmount: newInstallmentAmount,
                            }));
                          }}
                        />
                        {conversionData.installmentAmount &&
                          conversionData.totalInstallments > 1 && (
                            <p className="text-muted-foreground text-sm">
                              Total:{' '}
                              {formatCurrency(
                                Number(conversionData.installmentAmount) *
                                  conversionData.totalInstallments
                              )}
                            </p>
                          )}
                      </div>
                    ) : (
                      formData.amount &&
                      conversionData.totalInstallments > 1 && (
                        <p className="text-muted-foreground text-sm">
                          Parcelas de:{' '}
                          {formatCurrency(
                            Number(formData.amount) / conversionData.totalInstallments
                          )}
                        </p>
                      )
                    )}

                    <Button
                      type="button"
                      variant="default"
                      className="w-full bg-orange-600 hover:bg-orange-700"
                      disabled={convertToInstallments.isPending}
                      onClick={handleConvertToInstallments}
                    >
                      {convertToInstallments.isPending
                        ? 'Convertendo...'
                        : `Converter em ${conversionData.totalInstallments} Parcelas`}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Tags */}
            <div className="space-y-2">
              <Label>Tags</Label>
              <TagMultiSelect onChange={setTags} value={formData.tags} />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Observações</Label>
              <Textarea
                id="notes"
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder="Observações adicionais..."
                rows={3}
                value={formData.notes}
              />
            </div>

            <DialogFooter className="flex justify-between">
              <Button
                className="flex items-center gap-2"
                disabled={deleteTransaction.isPending}
                onClick={handleDelete}
                type="button"
                variant="destructive"
              >
                <Trash2 className="h-4 w-4" />
                {deleteTransaction.isPending ? 'Excluindo...' : 'Excluir'}
              </Button>

              <div className="flex gap-2">
                <Button
                  onClick={() => onOpenChange(false)}
                  type="button"
                  variant="outline"
                >
                  Cancelar
                </Button>
                <Button disabled={updateTransaction.isPending} type="submit">
                  {updateTransaction.isPending
                    ? 'Salvando...'
                    : 'Salvar Alterações'}
                </Button>
              </div>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
